import { GetObjectCommand, S3Client } from "@aws-sdk/client-s3";
import npyjs from "npyjs";
import { AWS_CONFIG } from "../movenet/params";

const cv = window.cv;
const screen = window.screen;

const s3Client = new S3Client(AWS_CONFIG);

/**
 * The Calibration class handles the process of determining start and stop lines
 * in a transformed 2D image (e.g. from a camera) based on depth information.
 * It integrates with AWS S3 to fetch a depth map stored in .npy format and
 * uses the depth data to calculate various distance metrics.
 */
export default class Calibration {
  /**
   * Constructs a new Calibration instance.
   * @param {number} width - The width of the source image.
   * @param {number} height - The height of the source image.
   * @param {function|null} [on_complete_callback=null] - An optional callback invoked when calibration completes.
   */
  constructor(width, height, on_complete_callback = null) {
    this.stop_point = null;
    this.start_point = null;
    this.start_point_extended = null;
    this.start_point_extended_cv = null;
    this.__is_calibrated = false;

    // Initialize an empty OpenCV Mat of the specified dimensions
    this.image = new cv.Mat(height, width, cv.CV_8UC4);

    // Prepare screen sizing for display
    const dvTopHeader = document.querySelector(".top-header-wrap");
    const screenHeight = screen.height - dvTopHeader.offsetHeight;
    let fullImg = new cv.Mat();
    const screenSize = new cv.Size(
      Math.floor(screen.width),
      Math.floor(screenHeight)
    );
    // const screenSize = new cv.Size(
    //   Math.floor(screen.width),
    //   Math.floor(screenHeight)
    // );

    // Resize the image to the full screen and display in a canvas with id "output"
    cv.resize(this.image, fullImg, screenSize, 0, 0, cv.INTER_AREA);
    cv.imshow("output", fullImg);

    // Optional callback to be triggered on complete
    this.on_complete_callback = on_complete_callback;

    // Depth data placeholders
    this.depthData = null;
    this.percentDepthData = 0;

    // Depth calibration factor for functional reach (depth values are incorrect, need scaling)
    this.depthCalibrationFactor = 3.0; // Default: multiply depth by 3 (24" -> 72")
  }

  /**
   * Retrieves a "birds-eye view" point, effectively warping an image-space
   * coordinate into a top-down coordinate system.
   * @param {Array|object} point - A [x,y] array or a cv.Point instance.
   * @returns {object} The warped cv.Point based on depth transformations.
   */
  get_birds_eye_view_point(point) {
    let p = point;
    // Ensure we have a cv.Point instance.
    // History: Original code (Feb 2023) had `if (!point instanceof cv.Point)` which
    // is a JS precedence bug — `!` binds before `instanceof`, so `(!point)` evaluates
    // to `false`, and `false instanceof cv.Point` is always `false`. The if-body never
    // executed, meaning raw keypoint objects ({x, y, score, name}) were passed straight
    // through to get_warped_point, which happened to work because it accesses point.x
    // and point.y directly.
    // When the precedence was fixed to `!(point instanceof cv.Point)`, the if-body
    // started executing for the first time — but used `point[0], point[1]` (array
    // indexing), which returns undefined for keypoint objects. This broke ALL BEV
    // calculations (gait step detection, TUG steps, crossing detection, stop line
    // proximity) by producing NaN/0 for every warped coordinate.
    // Fix: use point.x and point.y since all callers pass MoveNet keypoint objects.
    if (!(point instanceof cv.Point)) {
      p = new cv.Point(point.x, point.y);
    }
    return this.get_warped_point(p);
  }

  /**
   * Indicates whether or not calibration has been completed successfully.
   * @returns {boolean} True if calibration is complete, false otherwise.
   */
  complete() {
    return this.__is_calibrated;
  }

  /**
   * Computes the average of the middle fraction of sorted numeric values.
   * This discards outlier sections (both high and low) based on the fraction provided.
   * @param {number[]} values - An array of numeric values.
   * @param {number} [fraction=0.50] - The fraction of values to keep from the middle.
   * @returns {number} The average of the retained values; 0 if none are retained.
   */
  // averageMiddle(values, fraction = 0.5) {
  averageMiddle(values, fraction = 0.5) {
    if (!values.length) return 0;

    // Sort values numerically
    values.sort((a, b) => a - b);

    // Determine how many elements to remove on each side
    let removeCount = Math.floor(((1 - fraction) / 2) * values.length);
    let sliced = values.slice(removeCount, values.length - removeCount);

    // Compute average of the remaining elements
    let sum = sliced.reduce((acc, val) => acc + val, 0);
    return sliced.length ? sum / sliced.length : 0;
  }

  /**
   * Determines the start and stop lines in the image using the depth data.
   * This method finds a "stop" line at a known horizontal coordinate and then
   * searches for a corresponding "start" line (further away) by referencing
   * depth values.
   */
  determine_start_stop_points() {
    // We assume the "center" x-value of the camera image
    let baseX = parseInt(this.image.cols / 2);

    // Sample a small range of x-values around the base
    let stopXVals = [];
    for (let offset = -10; offset <= 10; offset += 1) {
      stopXVals.push(baseX + offset);
    }

    // Collect distance estimates for a known "stop line" region
    let stopDistances = [];
    let stop_line_y = 400; // Hard-coded vertical coordinate
    stopXVals.forEach((xVal) => {
      let dist = this.get_depth_distance(xVal, 1920 - stop_line_y) * 3.28;
      stopDistances.push(dist);
    });

    // Average out the stop distances
    // let stop_distance = this.averageMiddle(stopDistances, 0.5);
    let stop_distance = this.averageMiddle(stopDistances, 0.5);

    // Search for start points that are approximately 10 feet beyond the stop distance
    let startPoints = [];
    stopXVals.forEach((xVal) => {
      for (let y = 1920 - 1; y >= 0; y--) {
        let distance = this.get_depth_distance(xVal, y) * 3.28;
        if (
          distance >= stop_distance + 10 &&
          distance <= stop_distance + 10.1
        ) {
          startPoints.push({ x: xVal, y });
          break;
        }
      }
    });

    // Compute average Y for the start points from the middle fraction
    let allY = startPoints.map((pt) => pt.y);
    let finalY = parseInt(this.averageMiddle(allY, 0.3));

    // Store final cv.Points
    this.stop_point_cv = new cv.Point(baseX, 1920 - stop_line_y);
    this.start_point_cv = new cv.Point(baseX, finalY);

    // Create line boundaries (drawing references)
    this.stop_line_cv = {
      x1: Math.min(this.stop_point_cv.x - 50, 0),
      y1: this.stop_point_cv.y,
      x2: Math.max(this.stop_point_cv.x + 50, this.image.cols),
      y2: this.stop_point_cv.y,
    };

    this.start_line_cv = {
      x1: Math.min(this.start_point_cv.x - 50, 0),
      y1: this.start_point_cv.y,
      x2: Math.max(this.start_point_cv.x + 50, this.image.cols),
      y2: this.start_point_cv.y,
    };

    // Mark calibration as complete
    this.__is_calibrated = true;
  }

  get_line_at_distance(distance_meters) {
    const baseX = parseInt(this.image.cols / 2);
    let foundY = null;
    for (let y = this.image.rows - 1; y >= 0; y--) {
      let depth = this.get_depth_distance(baseX, y);
      if (depth >= distance_meters) {
        foundY = y;
        break;
      }
    }
    if (foundY === null) return null;

    let point_cv = new cv.Point(baseX, foundY);

    if (distance_meters === 13 * 0.3048 || distance_meters === 6 * 0.3048) {
      this.start_point_cv = point_cv;
      this.start_line_cv = {
        x1: 0,
        y1: foundY,
        x2: this.image.cols,
        y2: foundY,
      };
      return null;
    }

    this.stop_point_cv = point_cv;
    this.stop_line_cv = {
      x1: 0,
      y1: foundY,
      x2: this.image.cols,
      y2: foundY,
    };
    this.__is_calibrated = true;
  }

  /**
   * Fetches depth data (.npy file) from an S3 bucket and parses it into memory.
   * The method uses streaming to monitor and log read progress.
   * @async
   * @param {string} s3Bucket - The name of the S3 bucket.
   * @param {string} s3Key - The key (path) of the .npy file within the S3 bucket.
   * @returns {Promise<boolean>} Resolves to `true` if successful.
   */
  async get_depth_data(s3Bucket, s3Key) {
    try {
      const params = { Bucket: s3Bucket, Key: s3Key };
      const response = await s3Client.send(new GetObjectCommand(params));
      const { Body, ContentLength } = response;

      // Stream reading
      const reader = Body.getReader();
      let receivedLength = 0;
      const chunks = [];
      const approxSize = (920 * 1080 + 32) * 4; // Approx. total size in bytes
      const totalSize = Number(ContentLength) || approxSize;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        chunks.push(value);
        receivedLength += value.length;
        this.percentDepthData = Math.min(
          (receivedLength / totalSize) * 100,
          100
        );
        // console.log(`Data read: ${this.percentDepthData.toFixed(2)}%`);
      }

      // Combine all chunks into a single ArrayBuffer
      const arrayBuffer = new Uint8Array(receivedLength);
      let position = 0;
      for (let chunk of chunks) {
        arrayBuffer.set(chunk, position);
        position += chunk.length;
      }

      // Parse NPY data
      const npyReader = new npyjs();
      this.depthData = npyReader.parse(arrayBuffer.buffer);

      return true;
    } catch (error) {
      console.error("Error reading depth data:", error);
      throw error;
    }
  }

  /**
   * Retrieves a distance value from the depth map for a given (x, y) coordinate.
   * Returns 0 if the depth data is not available or coordinates are out of bounds.
   * @param {number} x - The x coordinate in image space.
   * @param {number} y - The y coordinate in image space.
   * @returns {number} The depth distance in meters (by default), or 0 on out-of-bounds.
   */
  get_depth_distance(x, y) {
    x = Math.round(x);
    y = Math.round(y);

    if (!this.depthData || !this.depthData.data) return 0;

    const partialRows = this.depthData.shape[0];
    const cols = this.depthData.shape[1];

    if (y < 1000) {
      return 0;
    }
    const partialY = y - 1000;

    // Check bounds for partialY and x
    if (partialY < 0 || partialY >= partialRows) return 0;
    if (x < 0 || x >= cols) return 0;

    // Flattened index
    const idx = partialY * cols + x;

    // console.log("Depth at (", x, ",", y, ") = ", this.depthData.data[idx]);
    return this.depthData.data[idx];
  }

  get_depth_distance_inches(x, y, allowFullImage = false, applyCalibration = false) {
    x = Math.round(x);
    y = Math.round(y);

    if (!this.depthData || !this.depthData.data) return 0;

    const partialRows = this.depthData.shape[0];
    const cols = this.depthData.shape[1];

    let adjustedY;

    if (allowFullImage) {
      // FUNCTIONAL REACH: Map full 1920 range to available 920 depth data
      adjustedY = Math.floor((y / 1920) * 920);
    } else {
      // GAIT: Keep original behavior with y < 1000 filter
      if (y < 1000) {
        return 0;
      }
      adjustedY = y - 1000;
    }

    if (adjustedY < 0 || adjustedY >= partialRows) return 0;
    if (x < 0 || x >= cols) return 0;

    const idx = adjustedY * cols + x;
    const depthMeters = this.depthData.data[idx];
    let depthInches = depthMeters * 39.37;

    // Apply calibration factor if requested (for functional reach)
    if (applyCalibration) {
      depthInches = depthInches * this.depthCalibrationFactor;
    }

    console.log(`Depth at (${x}, ${y}): ${depthInches} inches`);
    return depthInches;
  }

  /**
   * Transforms a point from image space to a hypothetical "room" space based on
   * the depth data. The warped y-value is derived from the depth distance.
   * @param {object} point - A cv.Point in image space.
   * @returns {object} A new cv.Point in "room" space, where x is unchanged and
   *                   y is scaled by the depth data value (converted to mm).
   */
  get_warped_point(point) {
    let px_warped = point.x;
    // Convert the depth distance to millimeters (1 m = 1000 mm; in code 1 m = 393.7 px?)
    let py_warped = this.get_depth_distance(point.x, point.y) * 393.7;
    return new cv.Point(px_warped, py_warped);
  }

  /**
   * Finds the y coordinate at the given distance (in meters) from the device and creates a bounding box there.
   * @param {number} distance_meters - The target distance in meters (e.g., 3.9624 for 13 feet).
   * @returns {object} Bounding box coordinates {x1, y1, x2, y2} at the found y position.
   */
  create_patient_box_at_distance(distance_meters) {
    // Use the center x of the image
    const baseX = parseInt(this.image.cols / 2);
    let foundY = null;
    // Scan vertically from bottom to top
    for (let y = this.image.rows - 1; y >= 0; y--) {
      let depth = this.get_depth_distance(baseX, y);
      if (depth >= distance_meters) {
        foundY = y;
        break;
      }
    }
    if (foundY === null) return null;
    // Create a bounding box 100px wide, 50px tall centered at baseX, foundY
    // const boxWidth = 100;
    // const boxHeight = 50;
    const x1 = baseX;
    const y1 = foundY;
    // const x2 = baseX;
    // const y2 = foundY;
    console.log(`Patient box at distance ${distance_meters}m: (${x1}, ${y1})`);
    return { x1, y1 };
  }
}
